import React, { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon, Type } from "@ebscois/react-component-library";
import { Input, InputLabel, FormWrapper } from "./_.styled";

import debounce from "lodash/debounce";

import { logFindOnPageInitiated } from "../../util/analytics";

import {
  MATCH_NAV_DIRECTION,
  findOnPageSearchBarAttr,
  findOnPageSearchBarDataTestIds,
} from "./utils/const";
import { makeSearchRegExp } from "./utils/searchTerm";
import { makeMatchesStats, getMatchingDOMNodes } from "./utils/parser";
import { clearMatchesDOM, highlightDOMMatches } from "./utils/dom";
import {
  useTelemetry,
  useParser,
  useTable,
  useMatchFocus,
  useMatchNavigation,
} from "./hooks";

const preventDefault = (event) => event.preventDefault();

const handleInputTelemetry = debounce((input, telemetryData) => {
  const { pageTitle: title, recordAn } = telemetryData ?? {};
  const searchRegExp = makeSearchRegExp(input);
  if (!searchRegExp) {
    return;
  }

  logFindOnPageInitiated({
    title,
    recordAn,
    searchTerm: input,
  });
}, 1500);

const FindOnPageSearchBar = ({
  preprocessedSections,
  isContentSplit,
  contentColumnRef,
  currentSectionIndex,
  openSectionByIndex,
  canSearchInTables,
  openTableById,
  telemetryData,
  findOnPageBtnConfig,
  findOnPageMode,
  autoFocus,
  isMobile,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [shouldShowMatchesCount, setShouldShowMatchesCount] = useState(false);

  useEffect(() => clearMatchesDOM, []);

  const { getAllMatches } = useParser({
    preprocessedSections,
    canSearchInTables,
    isContentSplit,
  });
  const { handleOpenTable } = useTable({ openTableById });
  const { handleTelemetry } = useTelemetry({ inputValue, telemetryData });
  const {
    matches,
    setMatches,
    activeSectionIndex,
    setActiveSectionIndex,
    activeMatchIndex,
    setActiveMatchIndex,

    handleMatchNavigation,
    activeMatchIndexOffset,
    matchesCount,
  } = useMatchNavigation({
    openSectionByIndex,
    handleOpenTable,
    handleTelemetry,
    currentSectionIndex,
    inputValue,
  });

  const highlightSectionMatches = useCallback(
    (searchRegExp, indexToHighlight = 0) => {
      const nodes = getMatchingDOMNodes(
        contentColumnRef?.current,
        searchRegExp,
        canSearchInTables
      );
      highlightDOMMatches(searchRegExp, nodes, indexToHighlight);
    },
    [contentColumnRef, canSearchInTables]
  );

  const { focusFirstMatch } = useMatchFocus({
    inputValue,
    currentSectionIndex,
    activeSectionIndex,
    activeMatchIndex,
    matches,
    handleOpenTable,
    highlightSectionMatches,
    openSectionByIndex,
    setActiveSectionIndex,
  });

  const runSearch = useCallback(
    (term) => {
      setShouldShowMatchesCount(false);
      clearMatchesDOM();

      const searchRegExp = makeSearchRegExp(term);
      if (!searchRegExp) {
        return;
      }

      highlightSectionMatches(searchRegExp);

      getAllMatches(searchRegExp).then((newMatches) => {
        const activeMatchIndex = 0;

        setMatches(newMatches);
        setActiveMatchIndex(activeMatchIndex);
        setActiveSectionIndex(currentSectionIndex);

        setShouldShowMatchesCount(true);
        focusFirstMatch({
          newMatches,
          searchRegExp,
          activeMatchIndex,
        });
      });
    },
    [
      setMatches,
      setActiveMatchIndex,
      setActiveSectionIndex,
      currentSectionIndex,
      highlightSectionMatches,
      getAllMatches,
      focusFirstMatch,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => runSearch(inputValue), [preprocessedSections]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runSearchDebounced = useCallback(debounce(runSearch), [runSearch]);

  const handlePrev = () => handleMatchNavigation(MATCH_NAV_DIRECTION.PREV);
  const handleNext = () => handleMatchNavigation(MATCH_NAV_DIRECTION.NEXT);

  const handleInput = (event) => {
    if (event.key === "Enter") {
      handleMatchNavigation(
        event.shiftKey ? MATCH_NAV_DIRECTION.PREV : MATCH_NAV_DIRECTION.NEXT
      );
      return;
    }

    const searchTerm = event.target.value;
    setInputValue(searchTerm);
    runSearchDebounced(searchTerm);
    handleInputTelemetry(searchTerm, telemetryData);
  };

  const controlsMessage = shouldShowMatchesCount
    ? makeMatchesStats(activeMatchIndexOffset + activeMatchIndex, matchesCount)
    : "";

  const placeholder = isMobile
    ? findOnPageBtnConfig.input_placeholder
    : "Enter term";

  return (
    <FormWrapper>
      <InputLabel>
        <Icon
          icon={{
            color: "white",
            glyph: findOnPageBtnConfig.glyph,
          }}
        />
        <Type
          element="label"
          htmlFor={findOnPageSearchBarAttr[findOnPageMode]}
          color="white"
          fontSize="sm"
          fontWeight="semibold"
          marginLeft="xs"
        >
          {findOnPageBtnConfig.title}
        </Type>
      </InputLabel>
      <Input
        autoFocus={autoFocus}
        id={findOnPageSearchBarAttr[findOnPageMode]}
        name={findOnPageSearchBarAttr[findOnPageMode]}
        data-testid={findOnPageSearchBarDataTestIds[findOnPageMode]}
        placeholder={placeholder}
        aria-label={findOnPageBtnConfig.title}
        value={inputValue}
        onChange={handleInput}
        onKeyPress={handleInput}
        onSubmit={preventDefault}
        cancelable
        onCancel={handleInput}
        controls
        controlsMessage={controlsMessage}
        onForwardSelect={handlePrev}
        onReverseSelect={handleNext}
      />
    </FormWrapper>
  );
};

const sectionType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  data: PropTypes.string,
});

sectionType.subSections = PropTypes.arrayOf(sectionType);

FindOnPageSearchBar.propTypes = {
  preprocessedSections: PropTypes.arrayOf(sectionType),
  isContentSplit: PropTypes.bool.isRequired,
  contentColumnRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  currentSectionIndex: PropTypes.number.isRequired,
  openSectionByIndex: PropTypes.func.isRequired,
  canSearchInTables: PropTypes.bool.isRequired,
  openTableById: PropTypes.func.isRequired,
  telemetryData: PropTypes.shape({
    pageTitle: PropTypes.string,
    recordAn: PropTypes.string,
  }).isRequired,
  findOnPageBtnConfig: PropTypes.shape({
    title: PropTypes.string,
    glyph: PropTypes.string,
    flag: PropTypes.bool,
  }).isRequired,
  findOnPageMode: PropTypes.string.isRequired,
};

FindOnPageSearchBar.displayName = "FindOnPageSearchBar";

export { findOnPageSearchBarAttr };
export default FindOnPageSearchBar;
